import React, { useState } from 'react';
import { Modal, Button, Table, Badge, Row, Col } from 'react-bootstrap';
import ArticleImageModal from './ArticleImageModal';
import SendEmailModal from './SendEmailModal';
import { downloadQuotePDF, printQuotePDF } from '../utils/pdfGenerator';
import { sendQuoteViaWhatsApp } from '../utils/whatsappHelper';

const QuoteDetailModal = ({ show, onHide, quote, user, sellers, onEditDraft }) => {
    const [selectedImageArticle, setSelectedImageArticle] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);

    const vendedorLabel = React.useMemo(() => {
        if (!quote) return '';
        if (quote.vendedor_nombre) return quote.vendedor_nombre;
        if (sellers && sellers.length > 0 && quote.c_vende) {
            const found = sellers.find(s => String(s.c_vende).trim() === String(quote.c_vende).trim());
            if (found) {
                const ape = (found.d_apell || '').trim();
                const nom = (found.d_nombr || '').trim();
                if (ape && nom) return `${ape}, ${nom}`;
                return ape || nom || `Vendedor ${quote.c_vende}`;
            }
        }
        return quote.c_vende ? `Vendedor ${quote.c_vende}` : (quote.c_usuar || 'General');
    }, [quote, sellers]);

    if (!quote) return null;

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    const formatQuantity = (amount) => {
        return new Intl.NumberFormat('es-AR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateStr;
    };

    const getIvaLabel = (c_iva) => {
        const mapping = {
            1: 'Resp. Inscripto',
            2: 'Resp. No Inscripto',
            3: 'Consumidor Final',
            4: 'Exento',
            5: 'Monotributo',
            6: 'No Responsable'
        };
        return mapping[c_iva] || `Condición ${c_iva || '-'}`;
    };

    const handleOpenImage = (item) => {
        setSelectedImageArticle({
            id: item.c_artic || item.id,
            name: item.d_artic || item.name,
            d_imagen: item.d_imagen,
            d_pathimg: item.d_pathimg,
            price: item.i_unita || item.price,
            rubroName: item.d_rubro || item.rubroName,
            notes: item.d_obser || item.notes
        });
        setShowImageModal(true);
    };

    const items = quote.items || [];
    const subtotalNeto = parseFloat(quote.i_grava) || parseFloat(quote.i_total) || 0;
    const noGravado = parseFloat(quote.i_nogra) || 0;
    const totalIVA = (parseFloat(quote.i_iva1) || 0) + (parseFloat(quote.i_iva2) || 0) + (parseFloat(quote.i_iva3) || 0) + (parseFloat(quote.i_ivaimp) || 0);
    const totalIIBB = parseFloat(quote.i_ingbru) || 0;
    const totalMunic = parseFloat(quote.i_munic) || 0;
    const totalImpub = parseFloat(quote.i_impub) || 0;
    const totalImint = parseFloat(quote.i_imint) || 0;
    const totalFinal = parseFloat(quote.i_total) || (subtotalNeto + noGravado + totalIVA + totalIIBB + totalMunic + totalImpub + totalImint);

    const handleDownloadPDF = () => {
        try {
            downloadQuotePDF(quote, user, sellers);
        } catch (error) {
            console.error("Error al descargar PDF:", error);
            alert("No se pudo generar el archivo PDF.");
        }
    };

    const handlePrintPDF = () => {
        try {
            printQuotePDF(quote, user, sellers);
        } catch (error) {
            console.error("Error al imprimir PDF:", error);
            alert("No se pudo abrir la vista previa de impresión.");
        }
    };

    return (
        <>
            <Modal show={show} onHide={onHide} size="xl" centered scrollable>
                <Modal.Header closeButton className="bg-primary text-white py-2">
                    <Modal.Title className="fs-6 d-flex align-items-center">
                        <i className="bi bi-file-earmark-text-fill me-2"></i>
                        <span>
                            {quote.is_borrador ? 'Borrador de Presupuesto:' : (quote.c_compr === 'NPT' ? 'Nota de Pedido de Tienda (NPT):' : 'Presupuesto:')} <strong>{quote.n_presu_label || `${quote.c_compr || 'PRE'} ${quote.n_presu || `ID ${quote.id}`}`}</strong>
                        </span>
                        {quote.t_presu && (
                            <Badge bg="light" text="dark" className="ms-2">
                                Tipo {quote.t_presu}
                            </Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="p-3 bg-light">
                    {/* CABECERA: DATOS DEL CLIENTE Y COMPROBANTE */}
                    <div className="card shadow-sm border-0 mb-3">
                        <div className="card-body p-3">
                            <Row className="g-3">
                                <Col md={6} lg={4}>
                                    <div className="text-muted small">Cliente</div>
                                    <div className="fw-bold text-dark">
                                        <span className="badge bg-secondary me-1">{quote.c_clien}</span>
                                        {quote.d_razon || 'Cliente General'}
                                    </div>
                                    {quote.n_cuit && (
                                        <small className="text-muted d-block">
                                            <strong>CUIT:</strong> {quote.n_cuit}
                                        </small>
                                    )}
                                    {quote.c_iva && (
                                        <small className="text-muted d-block">
                                            <strong>IVA:</strong> {getIvaLabel(quote.c_iva)}
                                        </small>
                                    )}
                                </Col>

                                <Col md={6} lg={4}>
                                    <div className="text-muted small">Domicilio y Contacto</div>
                                    <div className="fw-semibold text-dark">
                                        {(quote.d_domic || quote.d_entre || '-')}{quote.d_local ? `, ${quote.d_local}` : ''}
                                    </div>
                                    {quote.d_entre && (
                                        <small className="text-muted d-block">
                                            <i className="bi bi-truck me-1"></i>
                                            <strong>Dir. Entrega:</strong> {quote.d_entre}
                                        </small>
                                    )}
                                    <div className="d-none">
                                    </div>
                                    {(quote.n_telef || quote.n_celul) && (
                                        <small className="text-muted d-block">
                                            <i className="bi bi-telephone me-1"></i>
                                            {[quote.n_telef, quote.n_celul].filter(Boolean).join(' / ')}
                                        </small>
                                    )}
                                    {quote.d_email && (
                                        <small className="text-muted d-block">
                                            <i className="bi bi-envelope me-1"></i>
                                            {quote.d_email}
                                        </small>
                                    )}
                                </Col>

                                <Col md={12} lg={4}>
                                    <div className="bg-light p-2 rounded border">
                                        <div className="d-flex justify-content-between text-muted small">
                                            <span>Emisión:</span>
                                            <strong className="text-dark">{formatDate(quote.f_presu || quote.f_compr)}</strong>
                                        </div>
                                        <div className="d-flex justify-content-between text-muted small">
                                            <span>Vendedor:</span>
                                            <strong className="text-dark text-truncate ms-2" title={vendedorLabel}>{vendedorLabel}</strong>
                                        </div>
                                        {quote.n_lista && (
                                            <div className="d-flex justify-content-between text-muted small">
                                                <span>Lista Precios:</span>
                                                <strong className="text-dark">Lista {quote.n_lista}</strong>
                                            </div>
                                        )}
                                        {quote.d_flete && (
                                            <div className="d-flex justify-content-between text-muted small">
                                                <span>Flete / Transp.:</span>
                                                <strong className="text-dark text-truncate ms-2" title={quote.d_dirfle ? `${quote.d_flete} (${quote.d_dirfle})` : quote.d_flete}>
                                                    {quote.d_flete}
                                                </strong>
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {/* TABLA DE RENGLONES / PRODUCTOS */}
                    <div className="card shadow-sm border-0 mb-3">
                        <div className="card-header bg-white py-2 d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 fw-bold text-dark">
                                <i className="bi bi-cart3 text-primary me-2"></i>
                                Detalle de Artículos
                            </h6>
                            <span className="badge bg-primary-subtle text-primary border border-primary">
                                {items.length} Renglón(es)
                            </span>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive" style={{ maxHeight: '380px' }}>
                                <Table size="sm" hover className="mb-0 align-middle">
                                    <thead className="table-dark sticky-top" style={{ top: 0, zIndex: 10 }}>
                                        <tr style={{ fontSize: '0.85rem' }}>
                                            <th className="text-center" style={{ width: '40px' }}>#</th>
                                            <th style={{ width: '130px' }}>Producto</th>
                                            <th style={{ width: '90px' }} className="text-end">Cant.</th>
                                            <th style={{ width: '80px' }} className="text-center">Bultos</th>
                                            <th style={{ width: '120px' }} className="text-end">P. Unitario</th>
                                            <th style={{ width: '90px' }} className="text-center">Desc/Rec</th>
                                            <th style={{ width: '130px' }} className="text-end">Total</th>
                                            <th>Descripción / Notas</th>
                                        </tr>
                                    </thead>
                                    <tbody style={{ fontSize: '0.85rem' }}>
                                        {items.map((it, idx) => {
                                            const linePrice = parseFloat(it.i_unita || it.i_lista || it.i_publi || it.i_campre || it.price || 0);
                                            const lineQty = parseFloat(it.q_artic || it.quantity || 1);
                                            const lineDisc = parseFloat(it.p_desc2 || it.p_descu || it.discountSurcharge || 0);
                                            const lineTotal = (it.i_venta && parseFloat(it.i_venta) > 0)
                                                ? parseFloat(it.i_venta)
                                                : (it.i_total !== undefined && parseFloat(it.i_total) > 0)
                                                    ? parseFloat(it.i_total)
                                                    : (it.c_desrec === 'R' ? (linePrice * lineQty * (1 + Math.abs(lineDisc) / 100)) : (linePrice * lineQty * (1 - Math.abs(lineDisc) / 100)));

                                            return (
                                                <tr 
                                                    key={it.id || idx}
                                                    style={{ '--bs-table-bg': idx % 2 !== 0 ? '#fdf8ec' : '#ffffff' }}
                                                >
                                                    <td className="text-center text-muted fw-bold">{idx + 1}</td>
                                                    <td className="fw-bold text-primary">
                                                        <div className="d-flex align-items-center gap-1">
                                                            <span>{it.c_artic || it.id}</span>
                                                            <Button 
                                                                variant="outline-info" 
                                                                size="sm" 
                                                                className="rounded-circle d-flex align-items-center justify-content-center shadow-sm ms-auto"
                                                                style={{ width: '24px', height: '24px', padding: 0 }}
                                                                onClick={() => handleOpenImage(it)}
                                                                title="Ver imagen del artículo"
                                                            >
                                                                <i className="bi bi-eye" style={{ fontSize: '0.75rem' }}></i>
                                                            </Button>
                                                        </div>
                                                    </td>
                                                    <td className="text-end fw-bold">{formatQuantity(lineQty)}</td>
                                                    <td className="text-center text-muted">{it.q_bulto || it.boxes || 0}</td>
                                                    <td className="text-end fw-semibold">{formatCurrency(linePrice)}</td>
                                                    <td className="text-center text-muted">
                                                        {lineDisc ? `${it.c_desrec === 'R' ? '+' : (it.c_desrec === 'D' ? '-' : (lineDisc < 0 ? '-' : '+'))}${Math.abs(lineDisc)}%` : '0%'}
                                                    </td>
                                                    <td className="text-end fw-bold text-dark">{formatCurrency(lineTotal)}</td>
                                                    <td>
                                                        <div className="fw-semibold text-dark">{it.d_artic || it.name}</div>
                                                        {it.d_obser && (
                                                            <small className="text-muted fst-italic d-block">{it.d_obser}</small>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {items.length === 0 && (
                                            <tr>
                                                <td colSpan="8" className="text-center text-muted py-4">
                                                    Este presupuesto no contiene renglones cargados.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </div>

                    {/* PIE DE TOTALES E IMPUESTOS */}
                    <div className="row g-3">
                        <div className="col-md-6">
                            {quote.d_obser && (
                                <div className="card shadow-sm border-0 h-100">
                                    <div className="card-body p-3">
                                        <div className="text-muted small fw-bold mb-1">
                                            <i className="bi bi-card-text me-1"></i>Observaciones Generales:
                                        </div>
                                        <p className="mb-0 text-dark small bg-light p-2 rounded border">
                                            {quote.d_obser}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="col-md-6">
                            <div className="card shadow-sm border-0">
                                <div className="card-body p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1 text-muted small">
                                        <span>Subtotal Neto / Gravado:</span>
                                        <span className="fw-semibold">{formatCurrency(subtotalNeto)}</span>
                                    </div>
                                    {noGravado > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-1 text-muted small">
                                            <span>No Gravado / Exento:</span>
                                            <span className="fw-semibold">{formatCurrency(noGravado)}</span>
                                        </div>
                                    )}
                                    {totalIVA > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-1 text-muted small">
                                            <span>IVA Liquidado:</span>
                                            <span className="fw-semibold">{formatCurrency(totalIVA)}</span>
                                        </div>
                                    )}
                                    {totalIIBB > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-1 text-muted small">
                                            <span>Percepción IIBB:</span>
                                            <span className="fw-semibold">{formatCurrency(totalIIBB)}</span>
                                        </div>
                                    )}
                                    {totalMunic > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-1 text-muted small">
                                            <span>Tasa Municipal:</span>
                                            <span className="fw-semibold">{formatCurrency(totalMunic)}</span>
                                        </div>
                                    )}
                                    {totalImpub > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-1 text-muted small">
                                            <span>Impuesto Publicidad:</span>
                                            <span className="fw-semibold">{formatCurrency(totalImpub)}</span>
                                        </div>
                                    )}
                                    {totalImint > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-1 text-muted small">
                                            <span>Impuestos Internos:</span>
                                            <span className="fw-semibold">{formatCurrency(totalImint)}</span>
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-2">
                                        <span className="text-primary fw-bold fs-5">TOTAL:</span>
                                        <span className="text-primary fw-bold fs-5">
                                            {formatCurrency(totalFinal)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer className="bg-light py-2 d-flex justify-content-between">
                    <div className="d-flex gap-2">
                        {quote.is_borrador && onEditDraft && (
                            <Button
                                variant="warning"
                                size="sm"
                                onClick={() => {
                                    onHide();
                                    onEditDraft(quote);
                                }}
                                className="d-flex align-items-center gap-1 fw-bold shadow-sm text-dark"
                                title="Continuar editando este borrador en el Cotizador"
                            >
                                <i className="bi bi-pencil-square"></i>
                                <span>Editar Borrador</span>
                            </Button>
                        )}
                    </div>
                    <div className={quote.is_borrador ? 'd-none' : 'd-flex gap-2'}>
                        <Button 
                            variant="danger" 
                            size="sm" 
                            onClick={handleDownloadPDF} 
                            className="d-flex align-items-center gap-1 fw-bold shadow-sm"
                            title="Descargar presupuesto en formato PDF"
                        >
                            <i className="bi bi-file-earmark-pdf-fill"></i>
                            <span>Descargar PDF</span>
                        </Button>
                        <Button 
                            variant="outline-secondary" 
                            size="sm" 
                            onClick={handlePrintPDF} 
                            className="d-flex align-items-center gap-1 fw-bold shadow-sm"
                            title="Imprimir presupuesto"
                        >
                            <i className="bi bi-printer-fill"></i>
                            <span>Imprimir</span>
                        </Button>
                        <Button 
                            variant="outline-primary" 
                            size="sm" 
                            onClick={() => setShowEmailModal(true)} 
                            className="d-flex align-items-center gap-1 fw-bold shadow-sm"
                            title="Enviar presupuesto por correo electrónico al cliente"
                        >
                            <i className="bi bi-envelope-fill"></i>
                            <span>Enviar Email</span>
                        </Button>
                        {user?.l_enwhat !== false && (
                            <Button 
                                variant="outline-success" 
                                size="sm" 
                                onClick={() => sendQuoteViaWhatsApp({ quote, user, sellers })} 
                                className="d-flex align-items-center gap-1 fw-bold shadow-sm"
                                title="Enviar presupuesto por WhatsApp al cliente"
                            >
                                <i className="bi bi-whatsapp"></i>
                                <span>Enviar WhatsApp</span>
                            </Button>
                        )}
                    </div>
                    <Button variant="secondary" size="sm" onClick={onHide} className="px-4 fw-bold shadow-sm">
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Envío de Email */}
            <SendEmailModal 
                show={showEmailModal}
                onHide={() => setShowEmailModal(false)}
                quote={quote}
                user={user}
                sellers={sellers}
            />

            {/* Modal de Imagen para los artículos del detalle */}
            <ArticleImageModal 
                show={showImageModal} 
                onHide={() => setShowImageModal(false)} 
                article={selectedImageArticle} 
                baseImageUrl={user?.empresa_activa?.d_undisky}
            />
        </>
    );
};

export default QuoteDetailModal;
